/**
 * shadcn-style AlertDialog wrapper around Radix AlertDialog.
 *
 * Phase-2 Wave-2 W2.T4 primitive. Used for destructive-action confirms:
 * archive employee (research-A §2.3, T10/T12), role-transition confirm
 * (Q65), reissue-invite, last-* error surfacing.
 *
 * Why AlertDialog over Dialog: AlertDialog forces an explicit user choice
 * — the focus trap puts focus on the cancel button by default and ESC
 * dismisses, but the click-outside-to-close behaviour is intentionally
 * suppressed (Radix). This matches the "Promote to Admin?" /
 * "Archive last manager?" semantics where mis-clicks on the backdrop
 * MUST NOT silently close the dialog.
 */
import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-[rgba(20,24,31,0.40)] backdrop-blur-[2px]',
      'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
      className,
    )}
    {...props}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 grid w-[calc(100%-40px)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 gap-3 rounded-lg border border-line bg-card p-5 shadow-3 duration-base',
        // Колонка сетки задана явно — `minmax(0, 1fr)`, а не `auto`.
        // Без `grid-template-columns` единственная неявная колонка имеет
        // размер `auto`, чья МИНИМАЛЬНАЯ функция — min-content содержимого.
        // Название пункта каталога в 300 знаков одним словом (потолок CHECK
        // `section_catalog_item_name_length`, миграция 038) даёт min-content
        // ≈ 4200 px: колонка вырастает до 4200 px, ВСЕ элементы сетки
        // растягиваются под неё, и подвал с `sm:justify-end` уносит кнопки
        // на x ≈ 4400 при окне 1200 — мышью не достать. `minmax(0, 1fr)`
        // убирает минимум, и колонка не может стать шире самого окна.
        // Соседний `break-words` тут не спасает: `overflow-wrap: break-word`
        // по спецификации НЕ влияет на внутренний min-content размер.
        //
        // Колонку держит тест, а не соседний класс: снятие роняет 6 случаев
        // блока «тело окна» — «правый край подвала 5599 > правого края
        // диалога 355 (тело: nowrap)».
        //
        // ПОЧЕМУ `min-w-0` на детях долго выглядел ненаблюдаемым — и почему
        // он всё-таки нужен. По спецификации сеток автоминимум элемента
        // применяется, ТОЛЬКО ЕСЛИ минимальная функция его дорожки равна
        // `auto`. У `minmax(0,1fr)` она `0`, поэтому автоминимум обнуляется
        // САМ: колонка делает работу `min-w-0` ВМЕСТО него, а не заодно с
        // ним. Отсюда и ноль красных при снятии — искали содержимое, при
        // котором класс держит, а он держал НАСТРОЙКУ.
        //
        // У механизма есть условие, и оно ломается: `className` вызывающего
        // кода приходит ПРЯМО НА ЭТУ СЕТКУ, и стоит ему задать свои колонки
        // — минимальная функция снова `auto`, автоминимум возвращается,
        // запаса нет. Замерено, окно 1200, `className="grid-cols-[auto]"`,
        // узел с запретом переноса: подвал уезжает на 391…5949 при диалоге
        // 370…830, обе кнопки за экраном.
        //
        // Закрыто `[&>*]:min-w-0` — на КОНТЕЙНЕРЕ, а не на шапке с подвалом.
        // Разница проверена замером: класс на шапке и подвале спасает,
        // только когда враждебный узел лежит ВНУТРИ них; произвольное тело
        // от вызывающего кода — отдельный элемент сетки, и его такой класс
        // не касается (подвал остаётся на 5949). Правило на контейнере
        // накрывает любой прямой элемент, включая тело.
        //
        // У `Dialog` и `Sheet` этой дыры нет: там сетка и флекс живут во
        // ВНУТРЕННЕЙ области, куда `className` вызывающего кода не достаёт.
        'grid-cols-[minmax(0,1fr)] [&>*]:min-w-0',
        // Правило переноса стоит здесь, на контейнере, а не на заголовке с
        // описанием: свойство наследуемое, и так оно накрывает ЛЮБОЙ узел
        // тела, который положит вызывающий код. Пока правило висело на двух
        // узлах, тело окна не было защищено вовсе, а две копии подменяли
        // друг друга на тексте — снятие любой проходило незамеченным.
        //
        // `anywhere`, а не `break-words` (= `break-word`): разница не
        // косметическая. `break-word` НЕ участвует во внутреннем расчёте
        // min-content, поэтому непрерывное слово всё равно раздувало предка
        // (сетку окна, строку-флекс внутри заголовка) до своей полной ширины
        // и ломалось только визуально. `anywhere` уменьшает и min-content,
        // поэтому предок вправе сжаться. Наследование заодно распускает
        // вложенный `<span>` внутри флекс-заголовка
        // (archive-confirm-dialog.tsx), который иначе остаётся нерастяжимым
        // флекс-элементом.
        '[overflow-wrap:anywhere]',
        // Потолок высоты + собственная прокрутка. Окно центрировано
        // (`top-1/2 -translate-y-1/2`), поэтому без потолка длинное
        // содержимое — например, название пункта каталога в 300 знаков,
        // которое система разрешает, — растит окно в обе стороны от
        // центра, и кнопки уезжают за пределы экрана: снизу их не видно,
        // а прокручивается страница под окном, не само окно.
        'max-h-[calc(100dvh-40px)] overflow-y-auto overscroll-contain',
        'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-out',
        className,
      )}
      {...props}
    />
  </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1', className)} {...props} />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

// Подписи кнопок. `buttonVariants` ставит `whitespace-nowrap`, поэтому
// кнопка с длинной подписью нерастяжима: при подписи в 300 знаков ряд с
// `justify-end` уносил кнопки ВЛЕВО за край экрана (замерено при окне 1200:
// AlertDialog −4781, Dialog −4751, Sheet −4410) — тот же род отказа, что
// исходный, зеркально.
//
// Класс ОДИН, и это проверено мутацией. Рядом стояли `[&>*]:min-w-0` и
// `[&_button]:min-w-0`; снятие каждого по отдельности давало 70 из 70
// зелёных, и снятие обоих не меняло геометрию знаков подписи ни на точку.
// Они подменяли собой перенос, а своей границы не держали, — убраны по
// тому же правилу, что и `min-w-0` на шапке и подвале.
//
// Селектор — ПОТОМОК (`[&_button]`), а не прямой ребёнок. Прямого не
// хватало: в живом коде подвал бывает с обёрткой, и кнопки в нём подвалу
// внуки — `heatmap-day-detail-modal.tsx:443` кладёт
// `<div flex w-full justify-between>` с пояснением слева и рядом кнопок
// справа. Уезжает при этом НЕ подвал: замерено при снятой защите — подвал и
// обёртка держатся на 361…839 при диалоге 340…860, а ряд кнопок внутри
// обёртки уходит на 378…5968, сами кнопки на 378…3325 и 3333…5968. Прежние
// случаи оставались зелёными именно поэтому: они мерили подвал, а ломалось
// то, что внутри него.
//
// Почему наш класс побеждает `whitespace-nowrap` кнопки: `[&_button]` даёт
// селектор «класс + тип элемента» (0,1,1), одиночный `whitespace-nowrap` —
// (0,1,0). Специфичность СТРОГО выше, поэтому исход не зависит от порядка
// правил в таблице стилей. Проверено: вычисленный `white-space` у кнопки
// `normal` при том, что класс `whitespace-nowrap` на ней присутствует.
//
// `shrink` рядом с переносом — отдельная нужда, а не украшение. Вызывающий
// код вправе поставить `shrink-0` или `flex-none` (законный приём, когда
// кнопка короткая), и с длинной подписью это возвращало отказ целиком:
// замерено −4751 при окне 1200. Примитив обязан удержать границу независимо
// от классов вызывающего — тот же довод, что и с `bodyClassName`. На
// коротких подписях перебивание безвредно: раскладка совпадает до точки.
//
// Правило берёт и ссылки (`[&_a]`): в живом коде подвал бывает со ссылкой
// вместо кнопки — `employee-week-modal.tsx:194`. Общего «любой потомок»
// здесь не сделать: `[&_*]` даёт класс+универсальный, а универсальный
// специфичности не добавляет, и исход снова решал бы порядок правил.
//
// Снятие роняет случаи «подпись кнопки в 300 знаков», «подвал с обёрткой»,
// «видимый текст кнопки внутри диалога», «класс кнопки … не ломает подвал»
// и «подвал со ссылкой вместо кнопки».
const AlertDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
      '[&_a]:shrink [&_a]:whitespace-normal [&_button]:shrink [&_button]:whitespace-normal',
      className,
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    // Правило переноса намеренно НЕ дублируется здесь: оно стоит один раз
    // на `AlertDialogContent` и наследуется. Пока копий было две, снятие
    // любой из них проходило незамеченным — вторая подменяла первую.
    className={cn('text-base font-semibold tracking-tight text-ink', className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    // См. AlertDialogTitle — правило переноса живёт на контейнере.
    className={cn('text-sm text-muted', className)}
    {...props}
  />
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants(), className)} {...props} />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: 'outline' }), 'mt-0', className)}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
